import React from 'react';
import { useTranslation } from 'react-i18next';

const commonIssues = [
  {
    issue: 'Unable to register on APDPMS portal',
    solution: 'Ensure your Aadhaar number is linked to a valid mobile number. Clear browser cache and try using Chrome or Firefox. If the issue persists, contact the helpdesk.',
    icon: 'ri-user-add-line',
  },
  {
    issue: 'Payment failed but amount deducted',
    solution: 'Wait for 24-48 hours for auto-refund. If the refund is not received, submit a complaint with the transaction ID and bank statement screenshot to dtcp-ap@gov.in.',
    icon: 'ri-bank-card-line',
  },
  {
    issue: 'Unable to upload documents',
    solution: 'Ensure documents are in PDF format and each file is under 5 MB. Scanned documents should be clear and legible. Use a stable internet connection.',
    icon: 'ri-upload-cloud-line',
  },
  {
    issue: 'Application status showing "Pending" for long time',
    solution: 'Check if any clarifications or additional documents are requested in your application dashboard. Contact the respective UDA office for status update.',
    icon: 'ri-time-line',
  },
  {
    issue: 'How to correct mistakes in submitted application',
    solution: 'If the application is still under scrutiny, submit a request for modification through the portal. For applications already processed, contact the respective UDA office.',
    icon: 'ri-edit-line',
  },
  {
    issue: 'Building plan rejected - next steps',
    solution: 'Review the rejection remarks in your dashboard. Modify the plans as per the observations and resubmit. You can also file an appeal with the Joint Director, DTCP.',
    icon: 'ri-close-circle-line',
  },
];

export const HelpDeskPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.helpDesk')}</span>
      </nav>

      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{t('header.helpDesk')}</h1>
        <p className="text-lg text-stone-600 max-w-3xl">
          Get help with DTCP online services, application issues, and general queries. Our support team is available during working hours.
        </p>
      </div>

      {/* Contact Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-phone-line w-7 h-7 flex items-center justify-center text-amber-600"></i>
          </div>
          <h3 className="font-bold text-stone-800 mb-1">{t('contact.helpline')}</h3>
          <p className="text-xl font-bold text-amber-700">0863-2000662</p>
          <p className="text-xs text-stone-500 mt-2">Mon - Sat, 10:00 AM - 5:00 PM</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-mail-line w-7 h-7 flex items-center justify-center text-amber-600"></i>
          </div>
          <h3 className="font-bold text-stone-800 mb-1">{t('contact.emailSupport')}</h3>
          <p className="text-lg font-bold text-amber-700">dtcp-ap@gov.in</p>
          <p className="text-xs text-stone-500 mt-2">Response within 48 working hours</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-map-pin-line w-7 h-7 flex items-center justify-center text-amber-600"></i>
          </div>
          <h3 className="font-bold text-stone-800 mb-1">Visit Us</h3>
          <p className="text-sm font-medium text-amber-700">DTCP Head Office</p>
          <p className="text-xs text-stone-500 mt-2">Mangalagiri, Guntur District</p>
        </div>
      </div>

      {/* Common Issues */}
      <div className="mb-12">
        <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
          <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
          Common Issues & Solutions
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {commonIssues.map((item, index) => (
            <div key={index} className="bg-white border border-stone-200 rounded-lg p-5 hover:shadow-md hover:border-amber-300 transition-all">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-red-500`}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-1">{item.issue}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{item.solution}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Useful Links */}
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-6">
        <h2 className="text-lg font-bold text-stone-800 mb-4">Quick Links</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'APDPMS Portal', href: 'https://apdpms.ap.gov.in', icon: 'ri-building-2-line' },
            { label: 'Track Application', href: 'https://apdpms.ap.gov.in', icon: 'ri-search-line' },
            { label: 'FAQs', href: '/resources/faqs', icon: 'ri-question-answer-line' },
            { label: 'Training Materials', href: '/resources/training', icon: 'ri-video-line' },
          ].map((link, index) => (
            <a key={index} href={link.href} target={link.href.startsWith('http') ? '_blank' : undefined} rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined} className="flex items-center space-x-2 px-4 py-3 bg-white rounded-lg border border-stone-200 text-sm text-stone-700 hover:text-amber-700 hover:border-amber-300 transition-all">
              <i className={`${link.icon} text-amber-600`}></i>
              <span>{link.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};
